import { api } from '@/lib/axios';
import { logImageRecognitionJson } from '@/lib/logImageRecognitionJson';
import type { PatientEmail, PatientPhone, PatientProfile } from '@/store/patientProfileAtom';

// ── Create Account types ──────────────────────────────────────────────────────

export interface CreateAccountVerifyRequest {
  dateOfBirth: string;   // "YYYY-MM-DD"
  contact: string;       // email address or phone number
  contactType: 'email' | 'phone';
  /** True when the user explicitly requests a code resend; false (default) for the initial send. */
  isResend?: boolean;
  /** Guest scheduling only: sent on the retry after several patients matched. */
  firstName?: string;
  lastName?: string;
}
export interface CreateAccountVerifyResponse {
  /** false when no patient record matched the supplied DOB + contact. */
  found: boolean;
  message?: string;
  /** Several patients matched and no name was given. No code was sent. */
  requiresNameVerification?: boolean;
}

export interface CreateAccountValidateRequest {
  code: string;
}

/**
 * Guest ValidateCode / CreatePatient responses are shaped by the BFF into a guest-session response
 * (BffLoginResponse) on success — a 2xx with no explicit `valid` field already means success. On an
 * invalid code the upstream returns `{ valid: false, messageCode }` which passes through unchanged.
 */
export interface GuestValidateResponse {
  valid?: boolean;
  messageCode?: string;
  session?: string;
  csrfToken?: string;
  userName?: string;
  fullName?: string;
  patientKey?: number;
  userKey?: number;
}

export interface GenderOption {
  code: string;
  description: string;
}

export interface CreatePatientRequest {
  patient: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    gender?: string;
    email: string;
    phone: string;
    /** Marks the number as SMS-capable (mobile_flag). Omitted, the upstream treats it as mobile. */
    phoneIsMobile?: boolean;
  };
  /** Omitted entirely when the practice has contact information disabled or the guest skipped it. */
  address?: {
    address1: string;
    address2?: string;
    country?: string;
    city: string;
    state: string;
    zipCode: string;
  };
  /** Guest email-verification code. Sent only when GuestAppointmentEmailVerification is on. */
  verificationCode?: string;
}
export interface CreatePatientResponse {
  patientKey: number;
  // New-patient registration is guest-only; the BFF mediates it into a guest session.
  session?: string;
  csrfToken?: string;
  userName?: string;
  fullName?: string;
}

export interface CreateAccountPasswordRequest {
  username: string;
  password: string;
}

export interface SecurityQuestionAnswerRequest {
  customQuestion: string;
  answer: string;
}
export interface SaveSecurityQuestionsRequest {
  patientKey: number;
  answers: SecurityQuestionAnswerRequest[];
  token: string;
}

export interface SetPasswordResponse {
  success: boolean;
  errorCode?: string;
}

export interface SaveSecurityQuestionsResponse {
  success: boolean;
}

export interface PatientSecurityQuestion {
  customQuestion: string;
  answer: string;
}

export interface UpdateSecurityQuestionsRequest {
  patientKey: number;
  answers: SecurityQuestionAnswerRequest[];
}

// ── Update Patient Info types ─────────────────────────────────────────────────

export interface UpdatePatientInfoRequest {
  patientKey: number;
  patient: {
    namePrefixCode?: string;
    firstName: string;
    middleName?: string;
    lastName: string;
    nameSuffixCode?: string;
    dateOfBirth: string;
    gender?: string;
    marketingOptOut?: boolean;
  };
  address: {
    address1: string;
    address2?: string;
    country?: string;
    city: string;
    state: string;
    zipCode: string;
  };
  phones: Array<{ phoneKey: number; phoneNumber: string; isPrimary: boolean; isMobile: boolean }>;
  emails: Array<{ emailKey: number; emailAddress: string; isPrimary: boolean }>;
}

// ── Country state types ───────────────────────────────────────────────────────

export interface CountryStateOption {
  code: string;
  name: string;
}

// ── Patient ID OCR types ──────────────────────────────────────────────────────

export interface PatientOcrAddress {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export interface PatientOcrData {
  documentNumber?: string;
  firstName?: string;
  lastName?: string;
  /** ISO datetime string — convert to "yyyy-MM-dd" for the date input */
  dateOfBirth?: string;
  address?: PatientOcrAddress;
  genderCode?: string;
  /** Raw Image Recognition / Deephealth JSON — logged to F12 for OCR debugging. */
  imageRecognitionJson?: string;
}

// ── Service ───────────────────────────────────────────────────────────────────

/**
 * Builds the CreatePatient JSON body. Shared by the two registration calls, which differ
 * only in which BFF route — and therefore which session — they end up on.
 */
function postCreatePatient(path: string, req: CreatePatientRequest) {
  // Every address field is optional on the wire: the server null-guards each one, and the
  // patient may have skipped the address step (or never been shown it) entirely. Undefined
  // fields drop out of JSON.stringify, so omitted, null, and empty stay equivalent upstream.
  return api.post<CreatePatientResponse>(path, {
    patient: req.patient,
    ...(req.address          ? { address: req.address }                   : {}),
    ...(req.verificationCode ? { verificationCode: req.verificationCode } : {}),
  }).then(r => r.data);
}

export const patientService = {
  getProfile: (patientKey: number) =>
    api.get<PatientProfile>(`/Patient/${patientKey}/Profile`).then(r => r.data),

  identifyAccountCreation: (req: CreateAccountVerifyRequest) =>
    api.post<CreateAccountVerifyResponse>('/CreateAccount/IdentifyAccountCreation', req).then(r => r.data),

  validateAccountCreationCode: (req: CreateAccountValidateRequest) =>
    // Create-account path: a valid code is resolved to a patientKey held server-side in the
    // BFF HTTP session — the browser only ever sees { valid, messageCode }.
    api.post<{ valid?: boolean; messageCode?: string }>(
      '/CreateAccount/ValidateAccountCreationCode', req).then(r => r.data),

  // ── Guest scheduling identity (guest-only endpoints under /CreateAccount) ───────

  /** Look up an existing patient by DOB + contact; sends a guest validation code on success. */
  identifyGuestSchedule: (req: CreateAccountVerifyRequest) =>
    api.post<CreateAccountVerifyResponse>('/CreateAccount/IdentifyGuestSchedule', {
      dateOfBirth: req.dateOfBirth,
      contact: req.contact,
      contactType: req.contactType,
      ...(req.isResend ? { isResend: true } : {}),
      ...(req.firstName ? { firstName: req.firstName } : {}),
      ...(req.lastName ? { lastName: req.lastName } : {}),
    }).then(r => r.data),

  /** Validate the code for an existing guest patient — establishes a guest session on success. */
  validateGuestScheduleCode: (req: CreateAccountValidateRequest) =>
    api.post<GuestValidateResponse>('/CreateAccount/ValidateGuestScheduleCode', req).then(r => r.data),

  /**
   * Register a brand-new patient and establish a GUEST session (guest scheduling flow).
   * Same registration work as {@link createPatientAccountCreation} — only the session differs.
   */
  createPatientGuestSchedule: (req: CreatePatientRequest) =>
    postCreatePatient('/CreateAccount/CreatePatientGuestSchedule', req),

  /**
   * Register a brand-new patient for the CREATE ACCOUNT flow. The BFF seeds the
   * account-creation session key instead of minting a guest session, so the password step can
   * run — the patient is about to sign in properly rather than book as a guest.
   */
  createPatientAccountCreation: (req: CreatePatientRequest) =>
    postCreatePatient('/CreateAccount/CreatePatientAccountCreation', req),

  /** Emails a verification code to a prospective guest patient. The code itself stays server-side. */
  requestEmailVerification: (req: { email: string; isResend?: boolean }) =>
    api.post<{ sent: boolean }>('/CreateAccount/RequestEmailVerification', {
      email: req.email,
      ...(req.isResend ? { isResend: true } : {}),
    }).then(r => r.data),

  /** POST an ID / driver's-licence image to the guest OCR endpoint. */
  patientIdOcr: (file: File, options?: { signal?: AbortSignal }) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return api.post<PatientOcrData>('/CreateAccount/PatientIDOCR', form, {
      signal: options?.signal,
    }).then((r) => {
      logImageRecognitionJson('PatientIDOCR', r.data);
      return r.data;
    });
  },


  createAccountPassword: (req: CreateAccountPasswordRequest) =>
    api.post<SetPasswordResponse>('/CreateAccount/SetPassword', req).then(r => r.data),

  /**
   * Seeds the BFF's account-creation HTTP session from the CURRENT authenticated session's
   * patient (identity resolved server-side from the session JWT) — no token/patientKey is
   * returned to the browser. Used by the iCode 'O'/'R' booked-confirmation "Get Started" flow
   * to launch the create-account wizard directly on the password step.
   */
  issueCreateAccountToken: () =>
    api.post<Record<string, never>>('/CreateAccount/SessionToken', {}).then(r => r.data),

  saveSecurityQuestions: (req: SaveSecurityQuestionsRequest) =>
    api.post<SaveSecurityQuestionsResponse>('/CreateAccount/SaveSecurityQuestions', req).then(r => r.data),

  getSecurityQuestions: (patientKey: number) =>
    api.get<PatientSecurityQuestion[]>(`/Patient/${patientKey}/SecurityQuestions`).then(r => r.data),

  updateSecurityQuestions: (req: UpdateSecurityQuestionsRequest) =>
    api.put<void>(`/Patient/${req.patientKey}/SecurityQuestions`, { answers: req.answers }).then(r => r.data),

  checkUsername: (username: string) =>
    api.post<{ available: boolean }>('/CreateAccount/CheckUsername', { username }).then(r => r.data),

  /** Returns states from l_country_state for the address step state dropdown */
  getCountryStates: () =>
    api.get<CountryStateOption[]>('/Lookups/GetCountryState').then(r => r.data),

  /** Returns active gender options from l_gender */
  getGenders: () =>
    api.get<GenderOption[]>('/Lookups/Genders').then(r => r.data),

  /** Returns active name prefix options from l_name_prefix */
  getNamePrefixes: () =>
    api.get<GenderOption[]>('/Lookups/NamePrefixes').then(r => r.data),

  /** Returns active name suffix options from l_name_suffix */
  getNameSuffixes: () =>
    api.get<GenderOption[]>('/Lookups/NameSuffixes').then(r => r.data),

  /** PUT multipart/form-data — update patient demographics, address, phones, and emails in one call */
  updatePatientInfo: (req: UpdatePatientInfoRequest) => {
    const form = new FormData();
    if (req.patient.namePrefixCode) form.append('Patient.NamePrefixCode', req.patient.namePrefixCode);
    form.append('Patient.FirstName',   req.patient.firstName);
    if (req.patient.middleName) form.append('Patient.MiddleName', req.patient.middleName);
    form.append('Patient.LastName',    req.patient.lastName);
    if (req.patient.nameSuffixCode) form.append('Patient.NameSuffixCode', req.patient.nameSuffixCode);
    form.append('Patient.DateOfBirth', req.patient.dateOfBirth);
    if (req.patient.gender) form.append('Patient.Gender', req.patient.gender);
    if (req.patient.marketingOptOut !== undefined) form.append('Patient.MarketingOptOut', String(req.patient.marketingOptOut));
    form.append('Address.Address1',    req.address.address1);
    if (req.address.address2) form.append('Address.Address2', req.address.address2);
    if (req.address.country)  form.append('Address.Country',  req.address.country);
    form.append('Address.City',        req.address.city);
    form.append('Address.State',       req.address.state);
    form.append('Address.ZipCode',     req.address.zipCode);
    req.phones.forEach((p, i) => {
      form.append(`Phones[${i}].PhoneKey`,    String(p.phoneKey));
      form.append(`Phones[${i}].PhoneNumber`, p.phoneNumber);
      form.append(`Phones[${i}].IsPrimary`,   String(p.isPrimary));
      form.append(`Phones[${i}].IsMobile`,    String(p.isMobile));
    });
    req.emails.forEach((e, i) => {
      form.append(`Emails[${i}].EmailKey`,     String(e.emailKey));
      form.append(`Emails[${i}].EmailAddress`, e.emailAddress);
      form.append(`Emails[${i}].IsPrimary`,    String(e.isPrimary));
    });
    return api.put<void>(`/Patient/${req.patientKey}/UpdatePatientInfo`, form).then(r => r.data);
  },

  // ── Phone / Email read ────────────────────────────────────────────────────

  getPhones: (patientKey: number) =>
    api.get<PatientPhone[]>(`/Patient/${patientKey}/Phones`).then(r => r.data),

  getEmails: (patientKey: number) =>
    api.get<PatientEmail[]>(`/Patient/${patientKey}/Emails`).then(r => r.data),

  // ── Health Restriction ────────────────────────────────────────────────────

  getHealthRestriction: (patientKey: number) =>
    api.get<{ text: string | null }>(`/Patient/${patientKey}/HealthRestriction`).then(r => r.data),

  saveHealthRestriction: (patientKey: number, text: string) =>
    api.put<void>(`/Patient/${patientKey}/HealthRestriction`, { text }).then(r => r.data),

  clearHealthRestriction: (patientKey: number) =>
    api.delete<void>(`/Patient/${patientKey}/HealthRestriction`).then(r => r.data),
};
